import { COMING_SOON_LIMITS, UPDATE_LIMITS } from './constants';
import {
  ComingSoonFormErrors,
  UpdateFormErrors,
  WhatsNewComingSoonItem,
  WhatsNewUpdate
} from './types';

export const slugify = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const trimToLimit = (value: string, limit: number): string =>
  value.slice(0, limit).replace(/-+$/g, '');

/**
 * Matches the ids already in the stored config, which are a `YYYY-MM` prefix
 * taken from the publish month plus a slug of the title, e.g.
 * `2026-07-toc-editor-release`.
 */
export const buildUpdateId = (title: string, publishedAtIso: string): string => {
  const slug = slugify(title);
  if (!slug) {
    return '';
  }
  const publishedAt = publishedAtIso ? new Date(publishedAtIso) : null;
  const prefix =
    publishedAt && !Number.isNaN(publishedAt.getTime())
      ? `${publishedAt.getUTCFullYear()}-${String(publishedAt.getUTCMonth() + 1).padStart(2, '0')}-`
      : '';
  return trimToLimit(`${prefix}${slug}`, UPDATE_LIMITS.id);
};

export const buildComingSoonId = (title: string): string =>
  trimToLimit(slugify(title), COMING_SOON_LIMITS.id);

/** Trims a value and collapses empty strings to null, matching the optional API fields. */
export const toNullable = (value: string | null | undefined): string | null => {
  const trimmed = (value ?? '').trim();
  return trimmed.length ? trimmed : null;
};

/**
 * `datetime-local` inputs are naive, but the API requires timezone aware values.
 * Converting through `Date` stamps the browser's offset and emits UTC.
 */
export const localInputToIso = (value: string): string | null => {
  if (!value) {
    return null;
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
};

export const isoToLocalInput = (value: string | null): string => {
  if (!value) {
    return '';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '';
  }
  const offsetMs = parsed.getTimezoneOffset() * 60 * 1000;
  return new Date(parsed.getTime() - offsetMs).toISOString().slice(0, 16);
};

export const formatDisplayDateTime = (value: string | null): string => {
  if (!value) {
    return '';
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
};

const isValidHttpUrl = (value: string): boolean => {
  try {
    const parsed = new URL(value);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch (err) {
    return false;
  }
};

const requireText = (
  value: string,
  label: string,
  maxLength: number,
  required: boolean
): string => {
  const trimmed = value.trim();
  if (required && !trimmed.length) {
    return `${label} is required.`;
  }
  if (trimmed.length > maxLength) {
    return `${label} must be ${maxLength} characters or fewer.`;
  }
  return '';
};

/**
 * Mirrors `WhatsNewUpdate` validation, including the publish window rule.
 * `takenIds` holds the ids already in the list, so a new entry that collides with
 * one fails here rather than quietly replacing it on save.
 */
export const validateUpdate = (
  update: WhatsNewUpdate,
  takenIds: string[] = []
): UpdateFormErrors => {
  const errors: UpdateFormErrors = {};

  const id = requireText(update.id, 'ID', UPDATE_LIMITS.id, true);
  if (id) {
    errors.id = id;
  } else if (takenIds.includes(update.id.trim())) {
    errors.id = 'An update with this ID already exists.';
  }

  const title = requireText(update.title, 'Title', UPDATE_LIMITS.title, true);
  if (title) errors.title = title;

  const description = requireText(
    update.description,
    'Description',
    UPDATE_LIMITS.description,
    true
  );
  if (description) errors.description = description;

  const category = requireText(update.category ?? '', 'Category', UPDATE_LIMITS.category, false);
  if (category) errors.category = category;

  const content = requireText(update.content ?? '', 'Content', UPDATE_LIMITS.content, false);
  if (content) errors.content = content;

  const visualType = requireText(
    update.visual_type ?? '',
    'Visual type',
    UPDATE_LIMITS.visualType,
    false
  );
  if (visualType) errors.visual_type = visualType;

  const visualUrl = requireText(
    update.visual_url ?? '',
    'Visual URL',
    UPDATE_LIMITS.visualUrl,
    false
  );
  if (visualUrl) errors.visual_url = visualUrl;

  const ctaLabel = requireText(update.cta_label ?? '', 'CTA label', UPDATE_LIMITS.ctaLabel, false);
  if (ctaLabel) errors.cta_label = ctaLabel;

  const ctaUrl = (update.cta_url ?? '').trim();
  if (ctaUrl && !isValidHttpUrl(ctaUrl)) {
    errors.cta_url = 'CTA URL must be a valid http or https URL.';
  }

  if (!update.published_at) {
    errors.published_at = 'Published at is required.';
  }

  if (update.published_at && update.expires_at) {
    if (new Date(update.expires_at).getTime() <= new Date(update.published_at).getTime()) {
      errors.expires_at = 'Expires at must be later than published at.';
    }
  }

  return errors;
};

export const validateComingSoon = (
  item: WhatsNewComingSoonItem,
  takenIds: string[] = []
): ComingSoonFormErrors => {
  const errors: ComingSoonFormErrors = {};

  const id = requireText(item.id, 'ID', COMING_SOON_LIMITS.id, true);
  if (id) {
    errors.id = id;
  } else if (takenIds.includes(item.id.trim())) {
    errors.id = 'A coming soon item with this ID already exists.';
  }

  const title = requireText(item.title, 'Title', COMING_SOON_LIMITS.title, true);
  if (title) errors.title = title;

  const category = requireText(item.category ?? '', 'Category', COMING_SOON_LIMITS.category, false);
  if (category) errors.category = category;

  return errors;
};

export const hasErrors = (errors: Record<string, string | undefined>): boolean =>
  Object.values(errors).some(Boolean);

/** The API rejects duplicate ids across each list, so catch it before the PUT. */
export const findDuplicateIds = (ids: string[]): string[] => {
  const seen = new Set<string>();
  const duplicates = new Set<string>();
  ids.forEach(id => {
    if (seen.has(id)) {
      duplicates.add(id);
    }
    seen.add(id);
  });
  return Array.from(duplicates);
};

/**
 * Replaces the entry matching `item.id`, or appends it when the id is new.
 * Both lists save through the same path, so the upsert lives here rather than
 * being repeated per list in the page.
 */
export const upsertById = <T extends { id: string }>(list: T[], item: T): T[] => {
  const index = list.findIndex(entry => entry.id === item.id);
  if (index === -1) {
    return [...list, item];
  }
  const next = [...list];
  next[index] = item;
  return next;
};
